import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';

/**
 * A custom element for a shoppable "+" hotspot on an image.
 *
 * Clicking/tapping the hotspot always opens the quick-add drawer (the same
 * drawer used everywhere else in the theme), on every device. We used to
 * show a small hover popover on desktop instead, but it was unreliable — it
 * could close before the pointer reached it — so it was removed in favor of
 * this simpler, more robust interaction.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} [productLink] - The (hidden) product link element, used to resolve the product URL.
 *
 * @extends Component<Refs>
 */
export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger'];

  /**
   * Open the quick-add drawer for this hotspot's product.
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));

    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click - always opens the quick-add drawer.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.#openQuickAddModal();
  };
}

// Register custom element
customElements.define('product-hotspot-component', ProductHotspotComponent);